import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/inputs/Card';
import { MdClose } from 'react-icons/md';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import TextInput from 'components/inputs/Input';
import Button from 'components/inputs/Button';
import _ from 'lodash';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import queryString from 'query-string';
import { cookie } from 'utils/cookies/cookies';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_OPERATOR_MASTER, UPDATE_OPERATOR_MASTER } from 'actions/operatorMaster';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';
import { useTranslation } from 'react-i18next';

const OperatorMasterForm = (props) => {
  const { isEdit, onClose = _.noop(), onSuccess = _.noop() } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const operatorMasterDetail = useSelector((state) => state.operatorMaster?.operatorMasterDetail || {});
  const isLoading = useSelector((state) => state.operatorMaster?.isLoading || false);
  const token = cookie.get('token');

  const handleImage = (image, savePath, setFieldValue) => {
    if (!image) return;
    const fileName = _.get(image, 'name', '');

    axios({
      method: 'get',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: process.env.REACT_APP_API_URL + `/util/fileupload?name=${fileName}`,
    })
      .then((res) => {
        if (res.status === 201) {
          const signedRequest = _.get(res, 'data.signedRequest', '');
          handleUploadFile(signedRequest, image, savePath, setFieldValue);
        } else {
          toast.error(res.data?.message || 'File upload failed');
        }
      })
      .catch((e) => {
        console.error('File upload error -> ', e);
        toast.error('Image upload failed');
      });
  };

  const handleUploadFile = (url, imageData, savePath, setFieldValue) => {
    const fileType = _.get(imageData, 'type', '');

    axios({
      method: 'put',
      headers: {
        'Content-Type': fileType,
        'Content-Encoding': 'base64',
      },
      url: url,
      data: imageData,
    })
      .then((res) => {
        if (res.status === 200) {
          const imgUrlData = queryString.parseUrl(url);
          setFieldValue(savePath, imgUrlData.url);
          toast.success('Logo uploaded successfully');
        } else {
          toast.error(res.data?.message || 'Image upload failed');
        }
      })
      .catch((e) => {
        console.error('Image upload error -> ', e);
        toast.error('Image upload failed');
      });
  };

  const addOperator = useCallback((data) => {
    dispatch({
      type: ADD_OPERATOR_MASTER.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE || res?._id || res?.id) {
          onSuccess();
          onClose();
        }
      },
    });
  }, [dispatch, onClose, onSuccess]);

  const updateOperator = useCallback((data) => {
    dispatch({
      type: UPDATE_OPERATOR_MASTER.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === UPDATE_STATUS_CODE || res?._id || res?.id) {
          onSuccess();
          onClose();
        }
      },
    });
  }, [dispatch, onClose, onSuccess]);

  const OperatorMasterSchema = Yup.object().shape({
    name: Yup.string()
      .required('Operator name is required')
      .strict(true)
      .trim('Leading or trailing spaces not allowed')
      .max(100, 'Max 100 characters allowed'),
    website: Yup.string().nullable().url('Please enter a valid website URL (e.g. https://example.com)').optional(),
    logo_url: Yup.string().nullable().optional(),
    logo_thumbnail: Yup.string().nullable().optional(),
  });

  const initialValues = isEdit
    ? {
        id: operatorMasterDetail?._id || operatorMasterDetail?.id || '',
        name: operatorMasterDetail?.name || '',
        website: operatorMasterDetail?.website || '',
        logo_url: operatorMasterDetail?.logo_url || '',
        logo_thumbnail: operatorMasterDetail?.logo_thumbnail || '',
      }
    : {
        name: '',
        website: '',
        logo_url: '',
        logo_thumbnail: '',
      };

  return (
    <React.Fragment>
      <div className="stationAccessType-form-page__main">
        <Card>
          <div className="stationAccessType-form-header__block">
            <div className="stationAccessType-header-name">
              <span>{isEdit ? 'Edit Operator Master' : 'Add Operator Master'}</span>
            </div>
            <div className="close---btn">
              <MdClose size={30} color="#be210b" onClick={onClose} />
            </div>
          </div>
          <div className="stationAccessType-form-body__block">
            <div className="stationAccessType-form--block">
              <Formik
                enableReinitialize={!!isEdit}
                initialValues={initialValues}
                validationSchema={OperatorMasterSchema}
                onSubmit={(values, { setSubmitting }) => {
                  const payload = {
                    ...values,
                    name: values.name?.trim(),
                    website: values.website?.trim() || '',
                    logo_url: values.logo_url?.trim() || '',
                    logo_thumbnail: values.logo_thumbnail?.trim() || '',
                  };
                  if (isEdit) {
                    updateOperator(payload);
                  } else {
                    addOperator(payload);
                  }
                  setSubmitting(false);
                }}
              >
                {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col lg={12} md={12}>
                        <TextInput
                          isRequired
                          label="Operator Name"
                          placeholder="Enter operator name (e.g. Grevo)"
                          name="name"
                          value={_.get(values, 'name', '')}
                          onChange={handleChange}
                          error={errors.name && touched.name ? errors.name : null}
                        />
                      </Col>

                      <Col lg={12} md={12}>
                        <TextInput
                          label="Website URL"
                          placeholder="https://example.com"
                          name="website"
                          value={_.get(values, 'website', '')}
                          onChange={handleChange}
                          error={errors.website && touched.website ? errors.website : null}
                        />
                      </Col>

                      <Col lg={12} md={12}>
                        <TextInput
                          type="file"
                          label="Logo Image"
                          name="logo_file"
                          accept="image/*"
                          onChange={(e) => handleImage(e.currentTarget.files[0], 'logo_url', setFieldValue)}
                          image={values.logo_url}
                        />
                      </Col>

                      {/* <Col lg={12} md={12}>
                        <TextInput
                          label="Logo URL (or Direct Image Link)"
                          placeholder="https://storage.googleapis.com/..."
                          name="logo_url"
                          value={_.get(values, 'logo_url', '')}
                          onChange={handleChange}
                          error={errors.logo_url && touched.logo_url ? errors.logo_url : null}
                        />
                      </Col> */}

                      <Col lg={12} md={12}>
                        <TextInput
                          type="file"
                          label="Logo Thumbnail Image (Optional)"
                          name="logo_thumbnail_file"
                          accept="image/*"
                          onChange={(e) => handleImage(e.currentTarget.files[0], 'logo_thumbnail', setFieldValue)}
                          image={values.logo_thumbnail}
                          error={errors.logo_thumbnail && touched.logo_thumbnail ? errors.logo_thumbnail : null}
                        />
                      </Col>
                    </Row>
                    <div className="form_btn----block">
                      <Button className="form----btn cancel___btn" onClick={onClose}>
                        {t('button.cancel')}
                      </Button>
                      <Button type="submit" disabled={isSubmitting || isLoading} className="form----btn">
                        {isEdit ? 'Update' : 'Add'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Card>
      </div>
    </React.Fragment>
  );
};

OperatorMasterForm.propTypes = {
  onSuccess: PropTypes.func,
  onClose: PropTypes.func,
  isEdit: PropTypes.bool,
};

export default OperatorMasterForm;
